/** @format */

/**
 * IndexedDB Persister for React Query Cache
 *
 * Provides instant data on app restart and true offline support by persisting
 * the React Query cache to IndexedDB via idb-keyval.
 *
 * Benefits:
 * - Instant data display on app restart (before network requests complete)
 * - True offline support (data persists across browser sessions)
 * - Much higher storage limits than localStorage (~50MB+ vs 5MB)
 *
 * Performance:
 * - persistClient is throttled (trailing-edge, 2s) to batch rapid cache updates
 * - Persist and restore both run in a Web Worker to avoid blocking the main thread
 * - ColumnTable data is stored as plain objects (no Arrow IPC round-trip)
 * - Main thread only does cheap aq.from(objects) reconstruction on restore
 *
 * Notes:
 * - Arquero ColumnTable objects are stored as plain objects via table.objects()
 * - Version busting invalidates cache on app updates
 * - gcTime must be >= maxAge for persistence to work correctly
 */

import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import * as aq from "arquero";
import { del, get } from "idb-keyval";

import type {
  PersistWorkerMessage,
  PersistWorkerResponse,
} from "../workers/persistWorker";

// Bump version when changing serialization format to invalidate stale caches
const IDB_KEY = "galaxie-react-query-cache-v2";
const IDB_KEY_LEGACY = "galaxie-react-query-cache";
const ARROW_SENTINEL = "__arrowIPC__";
const TABLE_OBJECTS_SENTINEL = "__tableObjects__";

/** How long to wait after the last cache event before persisting (ms) */
const PERSIST_THROTTLE_MS = 2_000;

/** Timeout for worker restore operations (ms) */
const RESTORE_TIMEOUT_MS = 10_000;

/** Wrapper stored in IndexedDB for serialized Arrow data */
interface SerializedArrow {
  [key: string]: unknown;
  __arrowIPC__: true;
  bytes: Uint8Array;
}

/** Duck-type check for Arquero ColumnTable (avoids importing the class) */
function isColumnTable(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof (value as Record<string, unknown>).toArrow === "function" &&
    typeof (value as Record<string, unknown>).columnNames === "function"
  );
}

/**
 * Convert ColumnTable data to a plain-objects marker for the persistence worker.
 * This is cheap (just reads existing column arrays) — the expensive Arrow IPC
 * serialization happens in the worker.
 */
function prepareQueryDataForWorker(data: unknown): unknown {
  // Skip non-serializable data (Promises from pending queries, functions, etc.)
  if (data === undefined || data instanceof Promise || typeof data === "function") {
    return undefined;
  }
  if (isColumnTable(data)) {
    try {
      const table = data as { objects: () => Record<string, unknown>[] };
      return {
        [TABLE_OBJECTS_SENTINEL]: true,
        objects: table.objects(),
      };
    } catch {
      return undefined;
    }
  }
  return data;
}

/**
 * Reconstruct ColumnTable from plain objects returned by the worker.
 * This is cheap — aq.from() just wraps the array without Arrow overhead.
 */
function reconstructQueryData(data: unknown): unknown {
  if (
    data !== null &&
    typeof data === "object" &&
    (data as Record<string, unknown>)[TABLE_OBJECTS_SENTINEL] === true
  ) {
    try {
      const { objects } = data as { objects: Record<string, unknown>[] };
      return aq.from(objects);
    } catch {
      return undefined;
    }
  }
  return data;
}

/** Deserialize sentinel-marked Arrow IPC bytes → ColumnTable (fallback path) */
function deserializeQueryData(data: unknown): unknown {
  if (
    data !== null &&
    typeof data === "object" &&
    (data as Record<string, unknown>)[ARROW_SENTINEL] === true
  ) {
    try {
      const { bytes } = data as SerializedArrow;
      return aq.fromArrow(
        bytes instanceof Uint8Array
          ? bytes
          : new Uint8Array(bytes as ArrayLike<number>),
        { useProxy: true }
      );
    } catch {
      return undefined;
    }
  }
  return data;
}

/** Pending restore operations awaiting worker response */
const pendingRestores = new Map<
  string,
  { resolve: (value: PersistedClient | undefined) => void }
>();

/** Lazily create the persistence worker singleton */
let workerInstance: Worker | null = null;
function getPersistWorker(): Worker | null {
  if (workerInstance) return workerInstance;
  try {
    workerInstance = new Worker(
      new URL("../workers/persistWorker.ts", import.meta.url),
      { type: "module" }
    );
    workerInstance.onerror = e => {
      console.warn("[QueryPersister] Worker error:", e.message);
    };
    workerInstance.onmessage = (event: MessageEvent<PersistWorkerResponse>) => {
      const response = event.data;
      if (response.operation === "restore") {
        const pending = pendingRestores.get(response.id);
        if (pending) {
          pendingRestores.delete(response.id);
          if (response.success && response.client) {
            // Reconstruct ColumnTables from plain objects on the main thread (cheap)
            for (const query of response.client.clientState.queries) {
              query.state.data = reconstructQueryData(query.state.data);
            }
            pending.resolve(response.client as unknown as PersistedClient);
          } else {
            pending.resolve(undefined);
          }
        }
      }
      // "persist" responses are fire-and-forget, no pending map needed
    };
    return workerInstance;
  } catch {
    // Workers may not be available (e.g., some test environments)
    return null;
  }
}

/**
 * Restore the persisted client via the worker (Arrow deserialization off main thread).
 * Falls back to synchronous main-thread restore if the worker is unavailable.
 */
async function restoreViaWorker(worker: Worker): Promise<PersistedClient | undefined> {
  const id = `restore-${Date.now()}`;

  return new Promise<PersistedClient | undefined>(resolve => {
    pendingRestores.set(id, { resolve });

    const message: PersistWorkerMessage = {
      id,
      operation: "restore",
      idbKey: IDB_KEY,
    };
    worker.postMessage(message);

    // Timeout — fall back to undefined (will trigger fresh fetches)
    setTimeout(() => {
      if (pendingRestores.has(id)) {
        pendingRestores.delete(id);
        console.warn("[QueryPersister] Worker restore timeout");
        resolve(undefined);
      }
    }, RESTORE_TIMEOUT_MS);
  });
}

/**
 * Restore the persisted client synchronously on the main thread (fallback).
 */
async function restoreSynchronous(): Promise<PersistedClient | undefined> {
  const client = await get<PersistedClient>(IDB_KEY);
  if (!client) return undefined;

  for (const query of client.clientState.queries) {
    query.state.data = deserializeQueryData(query.state.data);
  }
  return client;
}

/**
 * Creates an IndexedDB persister for React Query cache.
 * Automatically handles Arquero ColumnTable serialization via Arrow IPC format.
 *
 * Both persistClient and restoreClient are offloaded to a Web Worker so that
 * Arrow IPC serialization/deserialization never blocks the main thread.
 */
export function createIDBPersister(): Persister {
  // Clean up legacy cache key from before Arrow serialization support
  del(IDB_KEY_LEGACY).catch(() => {});

  // Throttle state: trailing-edge debounce
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let latestClient: PersistedClient | null = null;

  function flushPersist() {
    persistTimer = null;
    const client = latestClient;
    latestClient = null;
    if (!client) return;

    const worker = getPersistWorker();
    if (!worker) return;

    try {
      // Prepare data for worker: convert ColumnTables to plain objects
      // This is cheap — the expensive Arrow serialization happens in the worker
      const preparedQueries = client.clientState.queries.map(query => ({
        ...query,
        state: {
          ...query.state,
          data: prepareQueryDataForWorker(query.state.data),
        },
      }));

      const message: PersistWorkerMessage = {
        id: `persist-${Date.now()}`,
        operation: "persist",
        idbKey: IDB_KEY,
        client: {
          buster: (client as unknown as { buster: string }).buster,
          timestamp: (client as unknown as { timestamp: number }).timestamp,
          clientState: {
            ...client.clientState,
            queries: preparedQueries,
          },
        },
      };

      worker.postMessage(message);
    } catch (error) {
      console.warn("[QueryPersister] Failed to send to worker:", error);
    }
  }

  return {
    persistClient: async (client: PersistedClient) => {
      // Trailing-edge throttle: always defer, persist the latest state
      latestClient = client;
      if (persistTimer === null) {
        persistTimer = setTimeout(flushPersist, PERSIST_THROTTLE_MS);
      }
    },
    restoreClient: async () => {
      try {
        const worker = getPersistWorker();
        if (worker) {
          return await restoreViaWorker(worker);
        }
        // Fallback: synchronous restore on main thread
        return await restoreSynchronous();
      } catch (error) {
        console.warn("[QueryPersister] Failed to restore cache:", error);
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await del(IDB_KEY);
      } catch (error) {
        console.warn("[QueryPersister] Failed to remove cache:", error);
      }
    },
  };
}

/**
 * Manually clear the persisted cache.
 * Useful for logout or cache invalidation scenarios.
 */
export async function clearPersistedCache(): Promise<void> {
  try {
    await del(IDB_KEY);
  } catch (error) {
    console.warn("[QueryPersister] Failed to clear cache:", error);
  }
}
